import { DomainUpsert } from "@/components/domain";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CreateRegion } from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
}: {
  model?: CreateRegion;
  onUpsert: (data: CreateRegion) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateRegion>>(() => {
    const config: FormOptions<CreateRegion> = {
      name: {
        value: model?.name ?? "",
        validator: z.string().min(1, "errors.required"),
      },
      color: {
        value: model?.color,
      },
      description: {
        value: model?.description ?? "",
      },
    };
    return config;
  }, [model]);

  return (
    <DomainUpsert
      title={model?.name ?? t("regions.new.title")}
      buttonTranslation={
        isEdit ? "regions.update.button" : "regions.new.button"
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "name",
          label: "regions.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name}
              onChange={(ev) => {
                form.setValue("name", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "color",
          label: "regions.fields.color",
          input: (form, id) => (
            <Input
              id={id}
              type="color"
              value={form.value.color ?? undefined}
              onChange={(ev) => {
                form.setValue("color", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "description",
          label: "regions.fields.description",
          input: (form, id) => (
            <Textarea
              className="w-full"
              id={id}
              value={form.value.description ?? ""}
              onChange={(ev) => {
                form.setValue("description", ev.target.value);
              }}
            />
          ),
        },
      ]}
    />
  );
}
